// src/components/MytripsLoginAuthFlow/useMytripsLoginFlow.ts
import { useEffect, useState } from "react";
import type { UseMytripsLoginFlow } from "@components/MytripsLoginAuthFlow/MyTripsLogin/contract";
import { REGISTER_PAGE_PATH } from "@components/User/LoginFastMenu/constants";
import { useRecaptchaV3 } from "@recaptcha/useRecaptchaV3";
import { trackLoaded } from "@tracking/index";
import {
  useAuthenticationFlows,
  useModalActions,
  useModalState,
} from "clientState/AuthFlow/providers/ModalScreenProvider";
import {
  screenNames,
  useScreenActions,
  useScreenState,
} from "clientState/AuthFlow/providers/ScreenProvider";
import type { ScreenNameType } from "clientState/AuthFlow/providers/ScreenProvider/contract";
import useNavigate from "clientState/useNavigate";
import { useTracking } from "@edreamsodigeo/frontend-core";

const { SIGNIN, PASSWORDLESS, NONE } = screenNames;

export const useMytripsLoginFlow = (
  successRedirectionUrl: string,
): UseMytripsLoginFlow => {
  const { state } = useScreenState();
  const { startFlow } = useAuthenticationFlows();
  const { selectedFlow, origin } = useModalState();
  const navigate = useNavigate();
  const [mytripsLoginFlow, setMytripsLoginFlow] =
    useState<ScreenNameType>(PASSWORDLESS);
  const {
    renderSuccessScreen,
    renderSigninScreen,
    renderForgotPasswordScreen,
    renderPasswordlessScreen,
    renderLoginSyncScreen,
  } = useScreenActions();
  const { closeModal } = useModalActions();
  const [email, setEmail] = useState(state.userData.email);
  const [acceptsNewsletter] = useState(state.userData.acceptsNewsletter);
  const { loadRecaptchaScript } = useRecaptchaV3();
  const { trackEvent } = useTracking();
  const { gaTrackingPrefix } = state;

  useEffect(() => {
    loadRecaptchaScript();
  }, [loadRecaptchaScript]);

  const onClose = (): void => {
    renderSigninScreen();
    closeModal();
  };

  const onSuccessNavigation = (): void => {
    navigate(successRedirectionUrl);
  };

  const openForgotPasswordFlow = (): void => {
    renderForgotPasswordScreen();
  };

  const onRenderPasswordless = (): void => {
    setMytripsLoginFlow(PASSWORDLESS);
    renderPasswordlessScreen();
    trackLoaded({
      trackEvent,
      screen: PASSWORDLESS,
      prefix: gaTrackingPrefix,
    });
  };

  const onRenderSignin = (): void => {
    setMytripsLoginFlow(SIGNIN);
    renderSigninScreen();
    trackLoaded({
      trackEvent,
      screen: SIGNIN,
      prefix: gaTrackingPrefix,
    });
  };

  const navigateToRegister = (): void => {
    navigate(REGISTER_PAGE_PATH);
  };

  if (selectedFlow === NONE) {
    startFlow({
      origin: "",
      emailLinkPath: successRedirectionUrl,
      isCloseable: true,
      isSuccessCloseable: true,
    });
    renderPasswordlessScreen();
  }

  return {
    origin,
    mytripsLoginFlow,
    email,
    setEmail,
    onClose,
    onSuccessNavigation,
    openForgotPasswordFlow,
    onRenderPasswordless,
    onRenderSignin,
    onRenderSuccessScreen: renderSuccessScreen,
    onRenderLoginSyncScreen: renderLoginSyncScreen,
    navigateToRegister,
    acceptsNewsletter,
  };
};

export default useMytripsLoginFlow;
